/**
 * ActivityTrace - the expandable per-step timeline layer of the chat
 * Activity_Indicator.
 *
 * A native `<button>` disclosure toggles a panel listing every associated
 * activity. Because the control is a real button, keyboard focus and Enter/Space
 * activation come for free (Requirements 13.3, 13.4). `aria-expanded` is bound to
 * the open state (Requirements 13.5, 13.6) and `aria-controls` points at the
 * panel it reveals.
 *
 * Expanded, the panel lists each activity ordered by ascending `ts` (ties broken
 * by receipt order via {@link orderActivities}, Requirement 9.3) and shows, for
 * each one: its presentation label (Requirement 9.2), its lifecycle status, and
 * its type-appropriate detail (Requirement 9.2) —
 *   - knowledge_base_query: each sanitized source label with its page number
 *     when present (Requirement 9.4),
 *   - tool_call / mcp_tool_call: the invoked tool name (Requirement 9.5),
 *   - memory_retrieval: each sanitized memory item label (Requirement 9.6).
 *
 * A `failed` activity is shown with a distinct icon-plus-text state ("Didn't
 * complete") that is separable from `started` and `completed` and never relies on
 * color alone (Requirements 14.2, 13.7). The disclosure renders only when the
 * message has one or more activities (Requirement 9.1).
 */
import { useId, useState } from 'react';
import { clsx } from 'clsx';
import type { Activity, ActivityStatus } from '@/types/chat';
import { getActivityPresentation, orderActivities } from './activityPresentation';

export interface ActivityTraceProps {
  /** The message's Activity_Trace. One row is rendered per activity. */
  activities: Activity[];
  /** Optional extra classes for layout. */
  className?: string;
}

/** The text label and icon shape used to present an activity's status. */
interface StatusPresentation {
  /** Visible status text (never color alone, Requirement 13.7). */
  text: string;
  /** Stable status icon identifier resolved to an SVG below. */
  icon: 'in-progress' | 'completed' | 'failed';
  /** Tailwind text-color class; a supplement to the icon + text, not the sole cue. */
  tone: string;
}

/**
 * Map a lifecycle status to its presentation. `failed` is deliberately given a
 * distinct icon and the explicit "Didn't complete" wording so it is
 * distinguishable from `started` and `completed` without relying on color
 * (Requirements 14.2, 13.7).
 */
function getStatusPresentation(status: ActivityStatus): StatusPresentation {
  switch (status) {
    case 'completed':
      return { text: 'Completed', icon: 'completed', tone: 'text-emerald-400' };
    case 'failed':
      return { text: "Didn't complete", icon: 'failed', tone: 'text-rose-400' };
    case 'started':
    default:
      return { text: 'In progress', icon: 'in-progress', tone: 'text-dark-400' };
  }
}

/**
 * Resolve a status icon identifier to a small decorative SVG. The icon is marked
 * `aria-hidden` because the adjacent visible status text is its text alternative.
 */
function StatusIcon({ icon }: { icon: StatusPresentation['icon'] }) {
  const common = {
    className: 'h-3.5 w-3.5 flex-shrink-0',
    viewBox: '0 0 20 20',
    fill: 'currentColor' as const,
    'aria-hidden': true as const,
  };

  switch (icon) {
    case 'completed':
      // Check circle
      return (
        <svg {...common}>
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.7-9.96a.75.75 0 00-1.2-.9l-3.24 4.32-1.52-1.51a.75.75 0 10-1.06 1.06l2.13 2.12a.75.75 0 001.13-.09l3.76-5z"
            clipRule="evenodd"
          />
        </svg>
      );
    case 'failed':
      // Triangle with exclamation — shape differs clearly from the check circle
      return (
        <svg {...common}>
          <path
            fillRule="evenodd"
            d="M8.49 2.98c.67-1.17 2.35-1.17 3.02 0l6.28 10.98c.66 1.17-.17 2.62-1.5 2.62H3.71c-1.33 0-2.17-1.45-1.5-2.62L8.49 2.98zM10 6.75a.75.75 0 01.75.75v3a.75.75 0 01-1.5 0v-3A.75.75 0 0110 6.75zm0 7.25a1 1 0 100-2 1 1 0 000 2z"
            clipRule="evenodd"
          />
        </svg>
      );
    case 'in-progress':
    default:
      // Hollow circle / spinner dot
      return (
        <svg {...common}>
          <path
            fillRule="evenodd"
            d="M10 4a6 6 0 100 12 6 6 0 000-12zM2.5 10a7.5 7.5 0 1115 0 7.5 7.5 0 01-15 0z"
            clipRule="evenodd"
          />
        </svg>
      );
  }
}

/** Chevron that rotates with the disclosure open state (decorative). */
function Chevron({ expanded }: { expanded: boolean }) {
  return (
    <svg
      className={clsx(
        'h-3.5 w-3.5 flex-shrink-0 transition-transform duration-150',
        expanded && 'rotate-90'
      )}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M7.21 5.23a.75.75 0 011.06.02l4 4.25a.75.75 0 010 1.02l-4 4.25a.75.75 0 11-1.08-1.04L10.73 10 7.19 6.27a.75.75 0 01.02-1.04z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Render the type-appropriate detail for one activity, or `null` when the
 * activity carries no renderable detail (Requirement 2.3 — such activities are
 * presented by label alone).
 */
function ActivityDetailRows({ activity }: { activity: Activity }) {
  const detail = activity.detail;
  if (!detail) {
    return null;
  }

  // knowledge_base_query: sanitized source labels with optional page numbers.
  if (detail.sources && detail.sources.length > 0) {
    return (
      <ul className="mt-1 space-y-0.5" data-testid="activity-detail-sources">
        {detail.sources.map((source, index) => (
          <li
            key={`${source.label}-${index}`}
            className="flex items-baseline gap-1 text-xs text-dark-300"
          >
            <span className="text-dark-500" aria-hidden="true">
              •
            </span>
            <span>
              {source.label}
              {typeof source.page === 'number' && (
                <span className="text-dark-400">, page {source.page}</span>
              )}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  // tool_call / mcp_tool_call: the invoked tool name.
  if (detail.toolName) {
    return (
      <p className="mt-1 text-xs text-dark-300" data-testid="activity-detail-tool">
        <span className="text-dark-400">Tool: </span>
        {detail.toolName}
      </p>
    );
  }

  // memory_retrieval: sanitized memory item labels.
  if (detail.items && detail.items.length > 0) {
    return (
      <ul className="mt-1 space-y-0.5" data-testid="activity-detail-items">
        {detail.items.map((item, index) => (
          <li
            key={`${item.label}-${index}`}
            className="flex items-baseline gap-1 text-xs text-dark-300"
          >
            <span className="text-dark-500" aria-hidden="true">
              •
            </span>
            <span>{item.label}</span>
          </li>
        ))}
      </ul>
    );
  }

  // agent_delegation (reserved): the delegated-to agent name.
  if (detail.agentName) {
    return (
      <p className="mt-1 text-xs text-dark-300" data-testid="activity-detail-agent">
        <span className="text-dark-400">Agent: </span>
        {detail.agentName}
      </p>
    );
  }

  return null;
}

/**
 * The expandable per-step Activity_Trace. Renders nothing when there are no
 * activities (Requirement 9.1).
 */
export function ActivityTrace({ activities, className }: ActivityTraceProps) {
  const [expanded, setExpanded] = useState(false);
  const panelId = useId();

  if (activities.length === 0) {
    return null;
  }

  const ordered = orderActivities(activities);

  return (
    <div className={clsx('text-xs', className)} data-testid="activity-trace">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        aria-controls={panelId}
        className={clsx(
          'inline-flex items-center gap-1 rounded px-1 py-0.5 text-dark-400',
          'transition-colors hover:text-dark-200',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
        )}
        data-testid="activity-trace-toggle"
      >
        <Chevron expanded={expanded} />
        <span>
          {expanded ? 'Hide activity' : 'Show activity'} ({ordered.length})
        </span>
      </button>

      {expanded && (
        <ol
          id={panelId}
          className="mt-1.5 space-y-1.5 border-l border-dark-700/60 pl-3"
          data-testid="activity-trace-panel"
        >
          {ordered.map((activity) => {
            const { label } = getActivityPresentation(activity);
            const statusPresentation = getStatusPresentation(activity.status);
            return (
              <li key={activity.id} data-testid="activity-trace-item">
                <div className="flex items-center gap-1.5">
                  <span className="font-medium text-dark-200">{label}</span>
                  <span
                    className={clsx(
                      'inline-flex items-center gap-1',
                      statusPresentation.tone
                    )}
                    data-testid="activity-trace-status"
                    data-status={activity.status}
                  >
                    <StatusIcon icon={statusPresentation.icon} />
                    <span>{statusPresentation.text}</span>
                  </span>
                </div>
                <ActivityDetailRows activity={activity} />
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
